import React, { useEffect, useRef } from "react";
import { setRequestMicCameraModalsData } from "../../reducers/common-reducer";
import { getParticipantIndex, sendCameraPermissionResponseToGuest, sendMicPermissionResponseToGuest } from "../../shared/dataTrackFunction";
import { AR_MARKUP_VIEW, CALL_BODY_SCREEN, DISABLE, ENABLED, EXTRACT_TEXT_VIEW, MARKUP_VIEW, MEASUREMENT_VIEW } from "../../constants/Constants";
import { useDispatch, useSelector } from "react-redux";
import CustomToast from "../UI/customToast";
import NotificationCustomToastModal from "../UI/Modal/NotificationCustomToastModal";
import { toast } from "react-toastify";
import { dualStreamEnabled, isSilentObserver } from "../../shared/utility";
import useLanguageChange from "../../hooks/useLanguageChange";
import { FIREBASE_EVENT_CATEGORY } from "../../constants/FirebaseConstants";
import { LogFirebase } from "../../services/firestore";

const RequestPermissionForMicOrCameraModal = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();

    const { requestMicCameraModalsData, callParticipants, currentCallBodyScreen } = useSelector((state) => state.commonReducer);
    const { markupInprogress, markupLiveStream, arMarkupLiveStream } = useSelector((state) => state.markup);
    const { extractTextInprogress } = useSelector((state) => state.extractText);
    const { measurementView } = useSelector((state) => state.measurement);

    const currentIdentities = useRef(new Set()); // To check toast is alredy shown or not
    const requestMicCameraModalsDataRef = useRef(new Set()); // To get letest mic/cam toast values without and react rendering cycle

    const closedRequestPermissionForMicCameraModal = (msg) => {
        const uniqueKey = `${msg.identity}_${msg.request_type}`; // use to identified the toast
        currentIdentities.current.delete(uniqueKey); // on close the toast remove toast identity from the list
        const updatedModals = requestMicCameraModalsDataRef?.current?.filter((modal) => {
            const modalUniqueKey = `${modal.identity}_${modal.request_type}`;
            return modalUniqueKey !== uniqueKey;
        }); // filter or remove mic/cam data
        requestMicCameraModalsDataRef.current = updatedModals; // set filter mic/cam data ref
        dispatch(setRequestMicCameraModalsData(updatedModals)); // set filter mic/cam data to the redux state variable
        LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "closed_request_permission_for_mic_cam_toast", {
            identity: msg.identity,
            requestType: msg.request_type == "1" ? "Mic" : "Camera",
        });
    };

    const getRequestedParticipantName = (msg) => {
        let guestName = getParticipantIndex(msg.identity);
        let silentObserverName = "";
        if (isSilentObserver(msg.identity)) {
            silentObserverName = getSilentObserverName(msg); // get the silent observer name
        }
        LogFirebase(
            FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA,
            "requested_participant_name",
            silentObserverName || msg.participant_name || guestName
        );
        return silentObserverName || msg.participant_name || guestName;
    };

    /* get the silent observer name */
    const getSilentObserverName = (msg) => {
        const guestName = msg.participant_name;
        const userData = callParticipants.find((user) => user.identity === msg.identity);
        const index = userData?.isSilentObserverindex > 1 ? ` ${userData.isSilentObserverindex}` : "";
        return `${guestName ? `${guestName} (` : ""}${localisedValues?.observer_lable}${index}${guestName ? ")" : ""}`;
    };

    /* get the Mic/Cam toast title */
    const getTheMicCamToastTitle = (msg) => {
        const name = getRequestedParticipantName(msg);
        const micOrCam = msg.request_type == "1" ? localisedValues?.microphone?.toLowerCase() : localisedValues?.camera?.toLowerCase();

        return localisedValues?.user_wants_to_start_their_camera?.replace("<ParticipantName>", name)?.replace("<micOrCam>", micOrCam);
    };

    /* send the denied Mic/Cam request data-track and remove toast */
    const denyRequestPermissionForMicCameraModal = (msg) => {
        // 1 mic, 2 Camera
        if (msg.request_type === "1") {
            LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "deny_request_permission_for_mic_cam", { mic: true, identity: msg.identity });
            sendMicPermissionResponseToGuest(msg.identity, DISABLE); // 0 for Rejected
        }
        if (msg.request_type === "2") {
            LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "deny_request_permission_for_mic_cam", { camera: true, identity: msg.identity });
            sendCameraPermissionResponseToGuest(msg.identity, DISABLE);
        }
        closedRequestPermissionForMicCameraModal(msg);
    };

    /* send the allow Mic/Cam request data-track and remove toast */
    const allowRequestPermissionForMicCameraModal = (msg) => {
        // 1 mic, 2 Camera
        if (msg.request_type === "1") {
            LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "allow_request_permission_for_mic_cam", { mic: true, identity: msg.identity });
            sendMicPermissionResponseToGuest(msg.identity, ENABLED); // 1 for allow
        }
        if (msg.request_type === "2") {
            LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "allow_request_permission_for_mic_cam", { camera: true, identity: msg.identity });
            sendCameraPermissionResponseToGuest(msg.identity, ENABLED);
            /* If a single stream is allowed and the agent grants video permission to one user, the other video permission requests are marked as denied.*/
            if (!dualStreamEnabled()) {
                setTimeout(() => {
                    requestMicCameraModalsDataRef?.current?.forEach((modalMsg) => {
                        if (msg.identity !== modalMsg.identity && modalMsg.request_type === "2") {
                            LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "remove_requested_cam_permission_toasts_for_single_stream", {
                                identity: msg.identity,
                            });
                            const uniqueKey = `${modalMsg.identity}_${modalMsg.request_type}`;
                            toast.dismiss(uniqueKey); // Dismiss the toast, and it will trigger the onClose function defined in the toast configuration.
                        }
                    });
                }, 200);
            }
        }
        closedRequestPermissionForMicCameraModal(msg);
    };

    const dismissedToasts = (data) => {
        data?.forEach((modal) => {
            const uniqueKey = `${modal.identity}_${modal.request_type}`;
            toast.dismiss(uniqueKey); // Remove all Mic/Cam permission request toasts when the call is disconnected.
        });
    };

    /* dismissed notification toasts if below screen is available */
    useEffect(() => {
        if (
            (currentCallBodyScreen === CALL_BODY_SCREEN.NOTIFY_SCREEN ||
                currentCallBodyScreen === CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN ||
                markupInprogress !== MARKUP_VIEW.OFF_VIEW ||
                extractTextInprogress !== EXTRACT_TEXT_VIEW.OFF_VIEW ||
                measurementView !== MEASUREMENT_VIEW.OFF_VIEW ||
                arMarkupLiveStream !== AR_MARKUP_VIEW.OFF_VIEW ||
                markupLiveStream) &&
            requestMicCameraModalsDataRef.current?.length > 0
        ) {
            LogFirebase(FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA, "dismissed_notification_toasts_on_screen_change", "all_toast_are_dismissed");
            dismissedToasts(requestMicCameraModalsDataRef.current);
        }
    }, [currentCallBodyScreen, markupInprogress, extractTextInprogress, measurementView, arMarkupLiveStream, markupLiveStream]);

    useEffect(() => {
        /* dismissed notification toast on disconnected the call */
        return () => {
            if (requestMicCameraModalsDataRef.current?.length > 0) {
                LogFirebase(
                    FIREBASE_EVENT_CATEGORY.REQUEST_MIC_CAMERA,
                    "dismissed_notification_toasts_on_disconnected_call",
                    "all_toast_are_dismissed"
                );
                dismissedToasts(requestMicCameraModalsDataRef.current);
            }
        };
    }, []);

    /* Handle Mic/Cam Toasts */
    useEffect(() => {
        if (requestMicCameraModalsData.length > 0) {
            requestMicCameraModalsDataRef.current = requestMicCameraModalsData;
        }
        requestMicCameraModalsData?.map((msg) => {
            const uniqueKey = `${msg.identity}_${msg.request_type}`; // NOTE: Set and get this type of unique identity for all your filters.
            if (!currentIdentities.current.has(uniqueKey)) {
                currentIdentities.current.add(uniqueKey);
                CustomToast.default(<NotificationCustomToastModal key={uniqueKey} />, {
                    toastId: uniqueKey,
                    onClose: (data) => {
                        console.log("auto_close_toast_data", data);
                        /* - This is triggered when the toast is dismissed. 
                           - But if it’s not manually removed by the user or a click.
                           - it’s due to the auto-close or toast.dismiss behavior of the toast. 
                        */
                        LogFirebase(FIREBASE_EVENT_CATEGORY.NOTIFICATION_TOAST, "auto_closed_toast_identity", {
                            identity: data.msg?.identity,
                            requestType: data.msg?.request_type == "1" ? "Mic" : "Camera",
                        });
                        denyRequestPermissionForMicCameraModal(data.msg);
                    },
                    data: {
                        msg,
                        title: getTheMicCamToastTitle(msg),
                        closeModal: (msg) => denyRequestPermissionForMicCameraModal(msg),
                        handleActionFunction: (msg) => allowRequestPermissionForMicCameraModal(msg),
                    },
                });
            }
        });
    }, [requestMicCameraModalsData]);

    return <></>;
};

export default RequestPermissionForMicOrCameraModal;
